import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from 'react-query';
import { ListBilletsQuery } from './Boletos.types';
import { DEFAULT_ORDER, DEFAULT_ROWS } from '..';
import { useServiceError } from '@/hooks';
import {
  createConfigBulletByBankAccount,
  deleteBankSlip,
  fileReturnBankSlip,
  generateByAccountReceivable,
  bankSlipConsultPdf,
  generateRemmitanceAllBankSlip,
  generateRemmitanceBankSlip,
  generateRemmitanceByWriteOff,
  getDetailBullet,
  individualTicketChange,
  listBullets,
  remittanceReturnProtocol,
  updateConfigBulletByBankAccount,
  writeOffBankSlip,
  bankSlipRequestPdf,
  bankSlipPublicPdf,
} from './Boletos.service';
import { message, notification } from 'antd';
import notificationError from '@/utils/functions/notificationError';
import { useQueryChanel } from '@/contexts/QueryChanelProvider';
import { QUERY_ACCOUNT_RECEIVABLE_DETAIL } from '../AccountReceivable/AccountReceivable.hooks';

export function useCreateConfigBulletByBankAccount() {
  const queryClient = useQueryClient();

  const mutation = useMutation(createConfigBulletByBankAccount, {
    onSuccess(_, data) {
      notification.success({
        message: 'Sucesso!',
        description: 'Configuração enviada com sucesso!',
      });

      queryClient.invalidateQueries([
        'bullet-config-detail',
        data.bankAccountId,
      ]);
    },
  });

  useServiceError(mutation.error);

  return mutation;
}

export function useUpdateConfigBulletByBankAccount() {
  const queryClient = useQueryClient();

  const mutation = useMutation(updateConfigBulletByBankAccount, {
    onSuccess(_, data) {
      notification.success({
        message: 'Sucesso!',
        description: 'Configuração enviada com sucesso!',
      });

      queryClient.invalidateQueries([
        'bullet-config-detail',
        data.bankAccountId,
      ]);
    },
  });

  useServiceError(mutation.error);

  return mutation;
}

export function useConfigBulletByBankAccount(
  bankAccountId: number,
  companyId?: number,
) {
  const query = useQuery(
    ['bullet-config-detail', bankAccountId, companyId],
    () => getDetailBullet(bankAccountId, companyId),
    {
      enabled: !!bankAccountId && !!companyId,
    },
  );

  useServiceError(query.error);

  return query;
}

export function generateBulletByAccountReceivable() {
  const queryChanel = useQueryChanel();

  const mutation = useMutation(generateByAccountReceivable, {
    onSuccess(_, data) {
      notification.success({
        message: 'Sucesso!',
        description: 'Boleto gerado com sucesso!',
      });

      queryChanel({
        invalidateQueries: [
          [`accountReceivable/detail`, data.accountReceivableId],
          [
            `accountReceivable/detail`,
            {
              id: data.accountReceivableId,
              installmentId: data.installmentReceivableId,
            },
          ],
        ],
        resetQueries: [['dashboard-data']],
      });
    },
  });

  useServiceError(mutation.error);

  return mutation;
}

export function useBulletsList(params?: ListBilletsQuery) {
  const query = useInfiniteQuery(
    ['bankSlip-list', { ...params }],
    ({ pageParam }) => {
      const req: ListBilletsQuery = {
        nextToken: pageParam,
        order: params?.order || DEFAULT_ORDER,
        rows: params?.rows || DEFAULT_ROWS,
        ...params,
      };

      return listBullets(req);
    },
    {
      getNextPageParam: (lastPage) => lastPage.nextToken,
      enabled: !!params?.companyId,
    },
  );

  useServiceError(query.error);

  return query;
}

export function useGenerateRemmitanceBankSlip() {
  const queryClient = useQueryClient();

  const mutation = useMutation(generateRemmitanceBankSlip, {
    onSuccess() {
      notification.success({
        message: 'Sucesso!',
        description: 'Geração de remessa gerada com sucesso!',
      });

      queryClient.invalidateQueries(['bankSlip-list']);
    },
    onError(error) {
      notificationError(error);
    },
  });

  return mutation;
}

export function useGenerateRemmitanceAllBankSlip() {
  const queryClient = useQueryClient();

  const mutation = useMutation(generateRemmitanceAllBankSlip, {
    onSuccess() {
      notification.success({
        message: 'Sucesso!',
        description: 'Geração múltipla de remessa gerada com sucesso!',
      });

      queryClient.invalidateQueries(['bankSlip-list']);
    },
    onError(error) {
      notificationError(error, {
        translate: (errors) =>
          errors.map((err) =>
            err.includes('Não foi possível gerar remessa')
              ? 'Para gerar remessa os boletos tem que estar nos status informados no modal de geração múltipla.'
              : err,
          ),
      });
    },
  });

  useServiceError(mutation.error);

  return mutation;
}

export function useBankSlipConsultPdf() {
  return useMutation(bankSlipConsultPdf, {
    onSuccess() {
      notification.success({
        message: 'Sucesso!',
        description: 'Geração do PDF do boleto gerado com sucesso',
      });
    },
    onError(error) {
      notificationError(error);
    },
  });
}

export function useBankSlipRequestPdf() {
  const queryChanel = useQueryChanel();
  return useMutation(bankSlipRequestPdf, {
    onSuccess() {
      queryChanel({
        invalidateQueries: [
          ['bankSlip-list'],
          [QUERY_ACCOUNT_RECEIVABLE_DETAIL],
        ],
      });
    },
    onError(error) {
      notificationError(error);
    },
  });
}

export function useDeleteBankSlip() {
  const queryClient = useQueryClient();

  const mutation = useMutation(deleteBankSlip, {
    async onSuccess() {
      notification.success({
        message: 'Sucesso!',
        description: 'Boleto excluído com sucesso!',
      });

      await queryClient.invalidateQueries('bankSlip-list');
    },
  });

  useServiceError(mutation.error);

  return mutation;
}

export function useReturnFile() {
  const mutation = useMutation(fileReturnBankSlip, {
    onSuccess(response) {
      notification.info({
        message: 'Resposta',
        description: response.message,
      });
    },
  });

  useServiceError(mutation.error);

  return mutation;
}

export function useRemitanceReturnProtocol() {
  const mutation = useMutation(remittanceReturnProtocol, {
    onSuccess() {
      message.success({
        content: 'Consulta feita com sucesso',
      });
    },
  });

  useServiceError(mutation.error);

  return mutation;
}

export function useWriteOffBankSlip() {
  const queryChanel = useQueryChanel();

  return useMutation(writeOffBankSlip, {
    async onSuccess() {
      notification.success({
        message: 'Sucesso!',
        description: 'Baixa solicitada com sucesso!',
      });

      queryChanel({
        resetQueries: [['bankSlip-list']],
      });
    },
    onError(error) {
      notificationError(error);
    },
  });
}

export function useGenerateRemmitanceByWriteOff() {
  const queryClient = useQueryClient();

  return useMutation(generateRemmitanceByWriteOff, {
    async onSuccess() {
      notification.success({
        message: 'Sucesso!',
        description: 'Remessa da baixa gerada com sucesso!',
      });

      await queryClient.invalidateQueries(['bankSlip-list']);
    },
    onError(error) {
      notificationError(error);
    },
  });
}

export function useIndividualTicketChange() {
  const queryChanel = useQueryChanel();

  return useMutation(individualTicketChange, {
    onSuccess() {
      notification.success({
        message: 'Sucesso!',
        description: 'Boleto alterado com sucesso!',
      });

      queryChanel({ invalidateQueries: [['bankSlip-list']] });
    },
    onError(error) {
      notificationError(error);
    },
  });
}

export function useBankSlipPublicPdf() {
  return useMutation(bankSlipPublicPdf, {
    onSuccess() {
      notification.success({
        message: 'Sucesso!',
        description: 'Geração do PDF do boleto gerado com sucesso',
      });
    },
    onError(error) {
      notificationError(error);
    },
  });
}
